import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, firstValueFrom, fromEvent, merge, timer } from 'rxjs';
import { filter, startWith, switchMap, tap, throttleTime } from 'rxjs/operators';
import { PmCoverageService } from '../../../leave-and-coverage/services/pm-coverage.service';
import { LocalStorageKeys } from '../../constants/local-storage.constants';
import { LeaveAndCoverageQueryParamsEnum } from '../../enums/leave-and-coverage-query-params.enum';
import { GeneralRoutePaths } from '../../enums/route-types.enum';
import { UserService } from '../user/user.service';
import { CoverageSessionService } from './coverage-session.service';

@Injectable({
  providedIn: 'root',
})
export class CoverageTimeoutService {
  private coverageSessionService = inject(CoverageSessionService);
  private pmCoverageService = inject(PmCoverageService);
  private userService = inject(UserService);
  private router = inject(Router);

  private timeoutHandlingInProgress = false;

  private readonly COVERING_INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000; // 1h

  constructor() {
    this.startTracking();
  }

  private startTracking(): void {
    if (!('locks' in navigator)) {
      return;
    }

    const activityInCurrentTab$ = this.userService.pointerMove$.pipe(
      throttleTime(10000),
      tap(() => this.setLastActivityTimestamp(Date.now()))
    );

    const activityInAnotherTab$ = fromEvent<StorageEvent>(window, 'storage').pipe(
      filter((event: StorageEvent) => event.key === LocalStorageKeys.LAST_COVERAGE_ACTIVITY_AT && !!event.newValue)
    );

    const activity$ = merge(activityInCurrentTab$, activityInAnotherTab$);

    this.coverageSessionService
      .isCoveringActiveObservable()
      .pipe(
        switchMap((isCoveringActive) => {
          if (!isCoveringActive) {
            this.clearLastActivityTimestamp();
            return EMPTY;
          }

          return activity$.pipe(
            startWith(null),
            switchMap(() => timer(this.COVERING_INACTIVITY_TIMEOUT_MS))
          );
        })
      )
      .subscribe(() => this.handleCoverageTimeout());
  }

  private handleCoverageTimeout(): void {
    if (this.timeoutHandlingInProgress || !this.coverageSessionService.isCoveringActive()) {
      return;
    }

    navigator.locks.request('coverage-timeout-handling', { ifAvailable: true }, async (lock) => {
      if (!lock || this.timeoutHandlingInProgress || !this.coverageSessionService.isCoveringActive()) {
        return;
      }

      this.timeoutHandlingInProgress = true;

      const coveredPmFullName = this.coverageSessionService.getCoveredPmFullName();

      try {
        await firstValueFrom(this.pmCoverageService.stopCovering());

        this.router.navigate([`/${GeneralRoutePaths.LEAVE_AND_COVERAGE}`], {
          queryParams: {
            [LeaveAndCoverageQueryParamsEnum.COVERING_TIMED_OUT]: coveredPmFullName,
          },
          replaceUrl: true,
        });
      } finally {
        this.clearLastActivityTimestamp();
        this.timeoutHandlingInProgress = false;
      }
    });
  }

  private setLastActivityTimestamp(timestamp: number): void {
    localStorage.setItem(LocalStorageKeys.LAST_COVERAGE_ACTIVITY_AT, String(timestamp));
  }

  private clearLastActivityTimestamp(): void {
    localStorage.removeItem(LocalStorageKeys.LAST_COVERAGE_ACTIVITY_AT);
  }
}
